import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
  ogImage?: string;
  schema?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  ogImage,
  schema,
}: SEOHeadProps) {
  const location = useLocation();
  const fullTitle = `${title} - 快猫视频`;
  const defaultCanonical = `https://www.kuaimaovideo.com${location.pathname}`;
  const finalCanonical = canonicalUrl || defaultCanonical;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    if (keywords) setMeta("keywords", keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute("href", finalCanonical);

    setMeta("og:title", fullTitle, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    setMeta("og:url", finalCanonical, true);
    if (ogImage) setMeta("og:image", ogImage, true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);

    let scriptEl = document.querySelector('script[type="application/ld+json"][data-seo="dynamic"]');
    if (!scriptEl && schema) {
      scriptEl = document.createElement("script");
      scriptEl.setAttribute("type", "application/ld+json");
      scriptEl.setAttribute("data-seo", "dynamic");
      document.head.appendChild(scriptEl);
    }
    if (scriptEl && schema) {
      scriptEl.textContent = JSON.stringify(schema);
    }

    return () => {
      const dynScript = document.querySelector('script[type="application/ld+json"][data-seo="dynamic"]');
      if (dynScript) dynScript.remove();
    };
  }, [fullTitle, description, keywords, finalCanonical, ogType, ogImage, schema]);

  return null;
}